// This code is same as login-callback from @okta/react. Somehow this code wasn't working
// even after bumping the version. Maybe an issue with my system (node_modules). Will check with
// mac and change accordingly.
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { OktaContext } from '@okta/okta-react';
import { useNavigate } from '@rocketlane/rocket-router';
import { useEffect, useState, useContext } from 'react';
import { AccountAPI, AuthenticationServiceProvider } from 'api/account';
import { LS_TEMPLATE_ID_KEY, RoutePaths } from 'constant/constant';
import { useCustomAuth } from 'hooks/use-custom-auth';
import { useQueryParams } from 'hooks/use-query-params';
import { InitWorkerApi } from 'initWorker';
import { useAccountMetaQuery } from 'queries';
import { ActiveTab, getOkta } from 'services';
import { exchangeCodeWithId, authenticateWithId, redirectToOAuth } from 'utils/oauth-redirect';
const OktaError = ({ error })=>{
    if (error.name && error.message) {
        return /*#__PURE__*/ _jsxs("p", {
            children: [
                error.name,
                ": ",
                error.message
            ]
        });
    }
    return /*#__PURE__*/ _jsx("p", {
        children: error.toString()
    });
};
export const LoginCallback = ({ errorComponent, loadingElement = null, onAuthResume })=>{
    var _sessionStorage_getItem;
    var _data_okta, _data_okta1;
    const replace = useNavigate();
    const oktaContext = useContext(OktaContext);
    const oktaAuth = oktaContext === null || oktaContext === void 0 ? void 0 : oktaContext.oktaAuth;
    const authState = oktaContext === null || oktaContext === void 0 ? void 0 : oktaContext.authState;
    const [callbackError, setCallbackError] = useState(null);
    const { setCustomAuth } = useCustomAuth();
    const { data } = useAccountMetaQuery();
    const params = useQueryParams();
    const code = params.get('code');
    const cookiesEnabled = data === null || data === void 0 ? void 0 : (_data_okta = data.okta) === null || _data_okta === void 0 ? void 0 : _data_okta.cookiesEnabled;
    const clientId = data === null || data === void 0 ? void 0 : (_data_okta1 = data.okta) === null || _data_okta1 === void 0 ? void 0 : _data_okta1.clientId;
    // Read from BOTH stores: Safari ITP / enterprise policy / incognito can clear sessionStorage
    // during the cross-origin hop to Cognito and back. If only sessionStorage is read it returns
    // null on those devices, the effect falls into the IdP-initiated branch and sends
    // code_verifier=undefined to Cognito → HTTP 400 → "Something went wrong".
    const codeVerifier = (_sessionStorage_getItem = sessionStorage.getItem('codeVerifier')) !== null && _sessionStorage_getItem !== void 0 ? _sessionStorage_getItem : localStorage.getItem('codeVerifier');
    const ErrorReporter = errorComponent || OktaError;
    useEffect(()=>{
        if (codeVerifier && !cookiesEnabled && (data === null || data === void 0 ? void 0 : data.okta.authenticationServiceProvider) === AuthenticationServiceProvider.COGNITO) {
            // COGNITO FLOW (SP-initiated). Gated on the provider too — presence of a verifier alone
            // must not route a non-Cognito account here, since a verifier can survive in localStorage
            // past a failed/abandoned login (that guard, not premature clearing, is what stops a stale
            // value being misused). The return below stops execution from falling through to
            // loginWithCognito() at the bottom of the effect, which would dispatch a SECOND exchange
            // for the same single-use auth code (the concurrent-branch race).
            const issuerDomain = data === null || data === void 0 ? void 0 : data.okta.issuerDomain;
            if (code && codeVerifier && issuerDomain) {
                AccountAPI.loginWithCognitoSSO(issuerDomain, code, `${window.location.origin}/implicit/callback`, codeVerifier, clientId).then((res)=>{
                    // Clear only AFTER a successful exchange — leaving the verifier in place on failure
                    // lets a transient-failure retry still use it (the code may not have been consumed).
                    sessionStorage.removeItem('codeVerifier');
                    localStorage.removeItem('codeVerifier');
                    const { access_token } = res;
                    window.location.href = `${window.location.origin}/login?token=${access_token}&cognito=${true}`;
                }).catch(setCallbackError);
            }
            return;
        }
        if (!cookiesEnabled && code && (data === null || data === void 0 ? void 0 : data.okta.authenticationServiceProvider) === AuthenticationServiceProvider.COGNITO) {
            // IdP-initiated login: no codeVerifier was generated by the frontend, so it's omitted from
            // the exchange (loginWithCognitoSSO drops the param when undefined instead of serialising
            // the literal string 'undefined'). Cognito doesn't enforce PKCE for codes issued without a
            // code_challenge. The return prevents the same fall-through race as above.
            const issuerDomain = data === null || data === void 0 ? void 0 : data.okta.issuerDomain;
            if (code && issuerDomain) {
                AccountAPI.loginWithCognitoSSO(issuerDomain, code, `${window.location.origin}/implicit/callback`, undefined, clientId).then((res)=>{
                    sessionStorage.removeItem('codeVerifier');
                    localStorage.removeItem('codeVerifier');
                    const { access_token } = res;
                    window.location.href = `${window.location.origin}/login?token=${access_token}&cognito=${true}`;
                }).catch(setCallbackError);
            }
            return;
        }
        if (!cookiesEnabled && (data === null || data === void 0 ? void 0 : data.okta.authenticationServiceProvider) !== AuthenticationServiceProvider.COGNITO) {
            // OKTA FLOW
            if (onAuthResume) {
                onAuthResume();
                return;
            }
            // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
            oktaAuth.handleLoginRedirect().then(()=>{
                const templateId = localStorage.getItem(LS_TEMPLATE_ID_KEY);
                localStorage.removeItem(LS_TEMPLATE_ID_KEY);
                if (templateId) {
                    replace(RoutePaths.documentTemplate.replaceAll(':templateId', templateId));
                    return;
                }
                if (authState === null || authState === void 0 ? void 0 : authState.isAuthenticated) {
                    var _ActiveTab_pop;
                    // Hard nav (window.location), not rocket-router's SPA replace: post-#17308 an SPA
                    // replace here lands before auth state propagates and the protected-route gate
                    // bounces back to home. A full reload re-reads the fresh session and renders the
                    // restored deep link.
                    const activeTab = ((_ActiveTab_pop = ActiveTab.pop()) !== null && _ActiveTab_pop !== void 0 ? _ActiveTab_pop : '/').replace(window.location.origin, '');
                    window.location.replace(activeTab || '/');
                }
            }).catch(setCallbackError);
            return;
        }
        if (!code || !clientId) {
            return;
        }
        const issuerDomain = data === null || data === void 0 ? void 0 : data.okta.issuerDomain;
        const redirectUri = `${window.location.origin}/implicit/callback`;
        const loginWithCognito = async ()=>{
            try {
                const res = await AccountAPI.loginWithCognitoSSO(issuerDomain, code, redirectUri, codeVerifier, clientId);
                const { access_token } = res;
                window.location.href = `${window.location.origin}/login?token=${access_token}&cognito=true`;
            } catch (error) {
                setCallbackError(error);
            }
        };
        const loginWithNitro = async ()=>{
            try {
                var _ActiveTab_pop;
                var _getOkta;
                const res = await exchangeCodeWithId(code, redirectUri, // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
                codeVerifier, clientId);
                const { accessToken } = res;
                await authenticateWithId(accessToken);
                setCustomAuth(true);
                (_getOkta = getOkta()) === null || _getOkta === void 0 ? void 0 : _getOkta.authStateManager.updateAuthState();
                InitWorkerApi.setData();
                const activeTab = ((_ActiveTab_pop = ActiveTab.pop()) !== null && _ActiveTab_pop !== void 0 ? _ActiveTab_pop : '/').replace(window.location.origin, '');
                // Use hard navigation to clear init worker's cached rejections from pre-login 401s.
                // SPA navigation (replace) would keep the stale cached state, causing LazyBootApp
                // to stay at PageLoading forever since useAccountDataQuery sees the cached rejection.
                window.location.href = activeTab;
            } catch (unused) {
                redirectToOAuth(clientId);
            }
        };
        if (cookiesEnabled) {
            loginWithNitro();
        } else {
            loginWithCognito();
        }
    }, [
        oktaAuth,
        onAuthResume,
        replace,
        authState,
        codeVerifier,
        data === null || data === void 0 ? void 0 : data.okta,
        code,
        clientId,
        cookiesEnabled,
        setCustomAuth
    ]);
    const authError = authState === null || authState === void 0 ? void 0 : authState.error;
    const displayError = callbackError || authError;
    if (displayError) {
        return /*#__PURE__*/ _jsx(ErrorReporter, {
            error: displayError
        });
    }
    return loadingElement;
};
